import { useMemo } from 'react';
import { useIntl } from 'react-intl';
import {
    overallDescriptionInputSchema,
    type OverallDescriptionDocType,
    type FindingDocType,
    type FindingPlanumDocType,
    type FindingProfileDocType
} from '@ast-data/shared';
import {
    dateColumn,
    type GridColDef,
    naturalSortComparator
} from '../../lib/dataGrid';
import { useRecordActionsColumn } from '../../components/useGridRowActions';
import { useAuditColumns } from '../../components/auditColumns';
import { HIDDEN_AUDIT_COLUMNS } from '../../components/auditFields';
import {
    useInlineEditColumn,
    type InlineField
} from '../../components/gridInlineEdit';
import { useIsoWeekColumn } from '../../components/isoWeekColumn';
import { usePersonUserId } from '../users/personUserId';
import {
    useDetailColumnHeader,
    useFindingRefFilterColumns
} from './findingRefLabels';
import { useFindingRefsColumn } from './useFindingRefsColumn';

/**
 * The Gesamtbeschreibung fields editable in the grid — one definition for the
 * columns and the save, like the Fotos (see useInlinePhotoFields).
 *
 * Only the two that a whole batch can share: who wrote them up, and what they
 * are called. The description and the Bemerkung are long prose, and the manual
 * Fläche/Schnitt is where THIS record was taken.
 *
 * The Bearbeiter carries its account link along, resolved by the same rule the
 * form saves by.
 */
export function useInlineOverallDescriptionFields(): InlineField[] {
    const { resolve } = usePersonUserId();

    return useMemo(
        () => [
            { field: 'designation' },
            {
                field: 'editor',
                editor: { kind: 'person' },
                derive: (value: unknown) => ({
                    editorId: resolve(String(value ?? ''))
                })
            }
        ],
        [resolve]
    );
}

/**
 * The rest of the record — in the column picker and the filter panel, hidden by
 * default. `editorId` and the reference id arrays stay out; the audit stamps
 * come from {@link useAuditColumns}, hidden the same way.
 */
const OVERALL_DESCRIPTION_DETAIL_FIELDS = [
    'description',
    'remark',
    'area',
    'trench',
    'planum',
    'profile'
] as const;

/** `columnVisibilityModel` entries hiding the detail columns by default. */
export const HIDDEN_OVERALL_DESCRIPTION_DETAIL_COLUMNS: Record<
    string,
    boolean
> = {
    ...Object.fromEntries(
        OVERALL_DESCRIPTION_DETAIL_FIELDS.map((field) => [field, false])
    ),
    ...HIDDEN_AUDIT_COLUMNS
};

export function useOverallDescriptionsColumns({
    findings,
    planums,
    profiles,
    canEdit,
    inlineFields = [],
    onEdit,
    onDelete,
    onHistory
}: {
    findings: FindingDocType[];
    planums: FindingPlanumDocType[];
    profiles: FindingProfileDocType[];
    canEdit: boolean;
    /** Which columns are edited in the grid — see useInlineOverallDescriptionFields. */
    inlineFields?: readonly InlineField[];
    onEdit: (row: OverallDescriptionDocType) => void;
    onDelete: (row: OverallDescriptionDocType) => void;
    onHistory: (row: OverallDescriptionDocType) => void;
}): GridColDef<OverallDescriptionDocType>[] {
    const intl = useIntl();
    const detailHeader = useDetailColumnHeader('overallDescription');
    const actionsColumn = useRecordActionsColumn<OverallDescriptionDocType>({
        canEdit,
        onEdit,
        onDelete,
        onHistory
    });

    const auditColumns = useAuditColumns<OverallDescriptionDocType>();
    const inlineEdit = useInlineEditColumn<OverallDescriptionDocType>(
        overallDescriptionInputSchema,
        canEdit
    );
    const inlineOf = (field: string) =>
        inlineFields.find((spec) => spec.field === field);
    const isoWeekColumn = useIsoWeekColumn<OverallDescriptionDocType>(
        (row) => row.recordedAt
    );

    const refsColumn = useFindingRefsColumn<OverallDescriptionDocType>({
        headerId: 'overallDescription.field.findings',
        findings,
        planums,
        profiles
    });
    // Hidden, filterable Befundnummer/Fläche/Planum/Profil columns for the
    // filter panel.
    const findingFilterColumns =
        useFindingRefFilterColumns<OverallDescriptionDocType>(
            findings,
            planums,
            profiles
        );

    return [
        {
            field: 'overallDescriptionNumber',
            headerName: intl.formatMessage({
                id: 'overallDescription.field.overallDescriptionNumber'
            }),
            width: 110,
            // Numeric-aware so "2" sorts before "10".
            sortComparator: naturalSortComparator,
            // Bold like the Fund-/Befundnummer — it is the row's key.
            cellClassName: 'grid-cell-bold'
        },
        // Straight after the pinned key column: when a record was taken is the
        // first thing anyone reads off a row (same order in every grid).
        isoWeekColumn,
        dateColumn<OverallDescriptionDocType>({
            field: 'recordedAt',
            headerName: intl.formatMessage({ id: 'common.recordedAt' }),
            width: 150
        }),
        {
            field: 'designation',
            headerName: intl.formatMessage({
                id: 'overallDescription.field.designation'
            }),
            flex: 1,
            minWidth: 160,
            ...(inlineOf('designation')
                ? inlineEdit('designation', inlineOf('designation'))
                : {})
        },
        { ...refsColumn, field: 'findings', flex: 1, minWidth: 240 },
        {
            field: 'editor',
            headerName: intl.formatMessage({
                id: 'overallDescription.field.editor'
            }),
            width: 130,
            ...(inlineOf('editor')
                ? inlineEdit('editor', inlineOf('editor'))
                : {})
        },
        ...findingFilterColumns,
        ...OVERALL_DESCRIPTION_DETAIL_FIELDS.map<
            GridColDef<OverallDescriptionDocType>
        >((field) => ({
            field,
            headerName: detailHeader(field),
            width: 160
        })),
        ...auditColumns,
        actionsColumn
    ];
}
